import Image from "next/image";
import styles from "./IntroSection.module.css";

type ProfileUI = {
  techTitle: string;
  technologies: { name: string; icon: string }[];
  stats: { value: string; label: string }[];
};

type Props = {
  name: string;
  handle: string;
  title: string;
  avatarSrc: string;
  status?: string;
  mail?: string;
  phone?: string;
  showUserInfo?: boolean;
  profileUI?: ProfileUI;
};

export function SimpleProfileCard({
  name,
  handle,
  title,
  avatarSrc,
  status = "Online",
  mail,
  phone,
  showUserInfo = false,
  profileUI,
}: Props) {
  const waHref = phone ? `https://wa.me/${phone.replace(/\D/g, "")}` : "";

  // ✅ AQUÍ van las constantes (dentro del componente, antes del return)
  const techTitle = profileUI?.techTitle ?? "Tech Stack";
  const technologies = profileUI?.technologies ?? [];
  const stats = profileUI?.stats ?? [];

  return (
    <div className="relative">
      <div className={styles.glowBorder} />

      <div className={`${styles.techCard} p-6 sm:p-8`}>
        <div className="flex items-center gap-5 mb-8">
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-[2px]">
              <div className="w-full h-full rounded-2xl bg-slate-900 overflow-hidden">
                <Image
                  src={avatarSrc}
                  alt={name}
                  width={160}
                  height={160}
                  className="h-full w-full object-cover"
                  priority
                />
              </div>
            </div>

            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-[3px] border-slate-900" />
          </div>

          <div className="min-w-0">
            <h3 className="text-xl font-bold text-white truncate">{name}</h3>
            <p className="text-slate-400 text-sm truncate">@{handle}</p>

            {showUserInfo && (mail || phone) && (
              <div className="mt-1 space-y-1 text-sm text-slate-400 min-w-0">
                {mail && (
                  <a
                    href={`mailto:${mail}`}
                    className="block truncate hover:text-slate-200 transition select-text"
                    title={mail}
                  >
                    {mail}
                  </a>
                )}

                {phone && (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate hover:text-slate-200 transition select-text"
                    title={phone}
                  >
                    {phone}
                  </a>
                )}
              </div>
            )}

            <p className="text-slate-400 text-sm mt-2">{title}</p>

            <span className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 bg-emerald-500/10 text-emerald-400 text-xs font-medium rounded-full">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
              {status}
            </span>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent mb-8" />

        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-4">
            {techTitle}
          </p>

          <div className="grid grid-cols-3 gap-3">
            {technologies.map((tech) => (
              <div
                key={tech.name}
                className={`${styles.techIcon} flex flex-col items-center gap-2 p-3 sm:p-4 rounded-xl bg-white/5 border border-white/5 cursor-default`}
              >
                <span className="text-2xl">{tech.icon}</span>
                <span className="text-xs text-slate-400 font-medium">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats mini – ocultos en mobile */}
        <div className="hidden sm:flex items-center justify-between mt-8 pt-6 border-t border-white/5">
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{stats[0]?.value ?? "2+"}</p>
            <p className="text-xs text-slate-500">{stats[0]?.label ?? "Years exp."}</p>
          </div>

          <div className="w-px h-8 bg-white/10" />

          <div className="text-center">
            <p className="text-2xl font-bold text-white">{stats[1]?.value ?? "5+"}</p>
            <p className="text-xs text-slate-500">{stats[1]?.label ?? "Projects delivered"}</p>
          </div>

          <div className="w-px h-8 bg-white/10" />

          <div className="text-center">
            <p className="text-2xl font-bold text-white">{stats[2]?.value ?? "Focus"}</p>
            <p className="text-xs text-slate-500">{stats[2]?.label ?? "Backend"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}